import styled from '@emotion/styled';

const Wrapper = styled.div`
  display: flex;
  padding-top: 4px;
`;

const Cell = styled.div<{ $active: boolean; $color: string }>`
  flex: 1;
  height: 4px;
  border-radius: 8px;
  background: ${({ $active, $color }) => ($active ? $color : '#2B2D33')};
`;

export function PasswordStrengthBar({ score, allRequirementsMet = true }: { score: number; allRequirementsMet?: boolean }) {
  // Only show green if score is high enough AND all requirements are met
  const color =
    score <= 4
      ? 'var(--error-red, #D64545)'
      : score <= 8 || !allRequirementsMet
        ? 'var(--warning-amber, #F9A825)'
        : 'var(--success-green, #2E7D32)';

  return (
    <Wrapper>
      {Array.from({ length: 12 }, (_, i) => (
        <Cell key={i} $active={i < score} $color={color} />
      ))}
    </Wrapper>
  );
}

/**
 * Calculate a strength score 0-12.
 *
 *  • Length up to 4 pts  (≥8 chars = +2, ≥12 = +2)
 *  • Lower-case          +1
 *  • Upper-case          +2   (adds entropy)
 *  • Digits              +2
 *  • Symbols             +1   (some extra entropy)
 *  • Character variety ≥6 unique chars  +2
 *
 * TOTAL POSSIBLE = 12
 */
export const calcScore = (pwd: string): number => {
  let score = 0;
  const len = pwd.length;

  // length
  if (len >= 8) score += 2;
  if (len >= 12) score += 2;

  // composition
  if (/[a-z]/.test(pwd)) score += 1;
  if (/[A-Z]/.test(pwd)) score += 2;
  if (/\d/.test(pwd)) score += 2;
  if (/[^A-Za-z0-9]/.test(pwd)) score += 1; // reduced from 3 → 1

  // uniqueness / entropy
  if (new Set(pwd).size >= 6) score += 2;

  return Math.min(score, 12);
};
